<template>
  <div id="note-editor-container">
    <v-container id="note-editor-card-container">
      <v-card
        class="mb-3"
        v-if="$_hasNoEntry"
      >
        <v-card-text>(No entries)</v-card-text>
      </v-card>
      <v-card
        outlined
        class="mb-3"
        v-for="(noteWithInfo, timelineSegmentMergedId) in $_filteredNotesWithInfo"
        v-bind:key="timelineSegmentMergedId"
      >
        <v-row dense>
          <v-col>
            <v-subheader>Text</v-subheader>
            <v-card-text>
              <general-textarea
                dense rows="1"
                v-bind:disabled="noteWithInfo.isLocked"
                v-model="noteWithInfo.text"
                v-on:focus="$_setCurrentTextareaElement($event.target)"
                v-on:input="$_updateCurrentTextareaHeight"
                v-on:keydown.stop
                v-on:blur="$_onBlurCurrentTextarea(timelineSegmentMergedId)"
              >
              </general-textarea>
            </v-card-text>
          </v-col>
        </v-row>
        <v-row no-gutters dense>
          <v-col cols="4">
            <v-subheader>Author Name(ID)</v-subheader>
            <v-card-text>{{ noteWithInfo.authorName }}({{ noteWithInfo.authorId }})</v-card-text>
          </v-col>
          <v-col cols="4">
            <v-subheader>Date</v-subheader>
            <v-card-text>{{ noteWithInfo.date.toLocaleString() }}</v-card-text>
          </v-col>
          <v-col cols="4">
            <v-subheader style="position: relative">
              <span>Refer to</span>
              <v-spacer></v-spacer>
              <v-btn
                outlined text small right
                v-bind:disabled="noteWithInfo.isLocked"
                v-on:click.stop="$_toggleSelectSegmentsCallbackRegistoration(noteWithInfo.timelineSegmentId, $event)"
                v-on:keydown.stop
              >
                Edit
              </v-btn>
            </v-subheader>
            <v-btn
              text small tile
              v-for="(timelineSegmentIdReferringTo, referenceId) in $data.$_timelineSegmentIdsReferringTo[timelineSegmentMergedId]"
              v-bind:key="referenceId"
              v-on:click.stop="selectTimelineSegmentAndSeek(timelineSegmentIdReferringTo)"
              v-on:keydown.stop
            >
              {{ timelineSegmentIdReferringTo.mergedId }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
    <div id="note-editor-filter-container">
      <v-btn
        id="note-editor-filter-button"
        tile x-small text outlined
        v-on:click.stop="$_openFilterMenu"
        v-on:keydown.stop
        v-bind:color="$_noteEditorFilterButtonColor"
      >
        <v-icon>mdi-filter</v-icon>
      </v-btn>
      <note-editor-filter-menu
        ref="noteEditorFilterMenu"
        id="note-editor-filter-menu"
        v-on:apply-filter="$_applyFilter"
      >
      </note-editor-filter-menu>
    </div>
  </div>
</template>

<style scoped>
#note-editor-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
}

#note-editor-card-container {
  flex-grow: 1;
  z-index: 0;
  height: 100%;
  overflow-y: scroll;
}

#note-editor-filter-container {
  flex-grow: 0;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

#note-editor-filter-button {
  height: 100%;
  width: 30px;
  padding: 0;
  pointer-events: auto;
}

#note-editor-filter-menu {
  pointer-events: auto;
}

.attached-reference {
  background-color: #f03e1e !important;
}
</style>

<script>
import NoteEditorFilterMenu from './NoteEditor/NoteEditorFilterMenu.vue';
import { NoteFilter } from './NoteEditor/NoteEditorFilterMenu.vue';
import NoteWithInfo from './NoteEditor/NoteWithInfo.js';
import { TimelineDataSet, TimelineSegmentId } from '../../modules/ScattData.js';
import NoteSegmentData from '../../modules/segmentDataTypes/NoteSegmentData.js';
import referencePool, { Reference } from '../../modules/referencePool.js';
import GeneralTextarea from '../Parts/Inputs/GeneralTextarea.vue';
import Color from '../../modules/Color.js';
import EditorDefinition from '../../modules/EditorDefinition.js';

const noteEditorFilterButtonColor = new Color(206, 144, 144, 0.8);

export const NoteEditorDefinition = new EditorDefinition(
  'ScattNoteEditor',
  'Note',
  timelineDataSet => {
    for (let timelineData of Object.values(timelineDataSet)) {
      if (timelineData.segmentDataType === NoteSegmentData) return true;
    }
    return false;
  },
);

export default {
  components: {
    NoteEditorFilterMenu,
    GeneralTextarea,
  },

  props: {
    timelineDataSet: { type: TimelineDataSet },
  },

  data() {
    return {
      $_currentTextareaElement: undefined,
      $_registerSelectSegmentsCallbackButtonElement: undefined,
      $_filter: NoteFilter.emptyInstance,
      $_timelineSegmentIdsReferringTo: {},
      $_onReferencePoolRegisterCallbackId: undefined,
      $_onReferencePoolUnregisterCallbackId: undefined,
    };
  },

  created() {
    this.$data.$_onReferencePoolRegisterCallbackId = referencePool.registerOnRegisterCallback(this.$_refreshTimelineSegmentIdsRefferingTo);
    this.$data.$_onReferencePoolUnregisterCallbackId = referencePool.registerOnUnregisterCallback(this.$_refreshTimelineSegmentIdsRefferingTo);
  },

  mounted() {
    this.$_refreshTimelineSegmentIdsRefferingTo();
  },

  destroyed() {
    if (this.$_isSelectSegmentsCallbackRegistered) {
      this.$_unregisterSelectSegmentsCallback();
    }
    referencePool.unregisterOnRegisterCallback(this.$data.$_onReferencePoolRegisterCallbackId);
    referencePool.unregisterOnUnregisterCallback(this.$data.$_onReferencePoolUnregisterCallbackId);
  },

  computed: {
    $_isSelectSegmentsCallbackRegistered() { return (this.$data.$_registerSelectSegmentsCallbackButtonElement !== undefined); },
    $_hasNoEntry() { return (Object.keys(this.$_filteredNotesWithInfo).length === 0); },
    $_notesWithInfo() {
      let notesWithInfo = {};
      for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
        if (timelineData.segmentDataType === NoteSegmentData) {
          let authorId = timelineData.authorId;
          let authorName = timelineData.authorName;
          for (let [ segmentId, segment ] of Object.entries(timelineData.segments)) {
            let timelineSegmentId = new TimelineSegmentId(timelineDataId, segmentId);
            let timelineSegmentMergedId = timelineSegmentId.mergedId;
            notesWithInfo[timelineSegmentMergedId] = new NoteWithInfo(timelineData.name, timelineSegmentId, segment, authorId, authorName);
          }
        }
      }
      return notesWithInfo;
    },
    $_filteredNotesWithInfo() {
      let filteredNotes = {};
      let allReferences = referencePool.getAllReferences();
      for (let [ timelineSegmentMergedId, noteWithInfo ] of Object.entries(this.$_notesWithInfo)) {
        let filter = this.$data.$_filter;
        if (filter.authorName && !noteWithInfo.authorName.includes(filter.authorName)) continue;
        if (filter.authorId && !noteWithInfo.authorId.includes(filter.authorId)) continue;
        if (filter.text && !noteWithInfo.text.includes(filter.text)) continue;
        if (Object.keys(filter.timelineSegmentIdsReferringTo).length > 0) {
          let included = false;
          for (let reference of Object.values(allReferences)) {
            for (let timelineSegmentIdReferringTo of Object.values(filter.timelineSegmentIdsReferringTo)) {
              if (
                (reference.referFrom.isSameAs(noteWithInfo.timelineSegmentId)) &&
                (reference.referTo.isSameAs(timelineSegmentIdReferringTo)) &&
                (reference.refClass === 'Note'))
              {
                included = true;
                break;
              }
            }
            if (included) break;
          }
          if (!included) continue;
        }
        if (filter.timeSince) {
          if (noteWithInfo.date < filter.timeSince) continue;
        }
        if (filter.timeUntil) {
          if (noteWithInfo.date > filter.timeUntil) continue;
        }
        filteredNotes[timelineSegmentMergedId] = noteWithInfo;
      }
      return filteredNotes;
    },

    $_noteEditorFilterButtonColor() {
      if (this.$data.$_filter.isEmpty()) {
        return 'none';
      } else {
        return noteEditorFilterButtonColor.styleString;
      }
    },
  },

  inject: [
    'registerSelectSegmentsCallback',
    'unregisterSelectSegmentsCallback',
    'registerReferences',
    'unregisterReferences',
    'setSegment',
    'selectTimelineSegmentAndSeek',
  ],

  methods: {
    /* private */
    $_setCurrentTextareaElement(textareaElement) {
      this.$data.$_currentTextareaElement = textareaElement;
      this.$_updateCurrentTextareaHeight();
    },

    $_updateCurrentTextareaHeight() {
      if (this.$data.$_currentTextareaElement === undefined) return;
      this.$data.$_currentTextareaElement.style.height = '';
      this.$nextTick(() => {
        this.$data.$_currentTextareaElement.style.height = `${this.$data.$_currentTextareaElement.scrollHeight}px`;
      });
    },

    $_unsetCurrentTextareaElement() {
      this.$data.$_currentTextareaElement = undefined;
    },

    $_saveNote(timelineSegmentMergedId) {
      let noteWithInfo = this.$_filteredNotesWithInfo[timelineSegmentMergedId];
      let noteTimelineSegmentId = noteWithInfo.timelineSegmentId;
      let noteTimelineDataId = noteTimelineSegmentId.timelineDataId;
      let noteSegmentId = noteTimelineSegmentId.segmentId;
      let lastSavedNoteText = this.timelineDataSet[noteTimelineDataId].segments[noteSegmentId].data.text;
      if (noteWithInfo.text !== lastSavedNoteText) {
        let noteData = new NoteSegmentData.contentData(noteWithInfo.text, new Date());
        let noteDataSegment = new NoteSegmentData(noteWithInfo.begin, noteWithInfo.end, noteData);
        this.setSegment(noteTimelineSegmentId, NoteSegmentData, noteDataSegment, 'Note set.', 'setting note');
      }
    },

    $_onBlurCurrentTextarea(timelineSegmentMergedId) {
      this.$_saveNote(timelineSegmentMergedId);
      this.$_unsetCurrentTextareaElement();
    },

    $_toggleSelectSegmentsCallbackRegistoration(referFrom, event) {
      let registerSelectSegmentsCallbackButtonElement = event.currentTarget;
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement === registerSelectSegmentsCallbackButtonElement) {
        this.$_unregisterSelectSegmentsCallback();
      } else {
        if (this.$_isSelectSegmentsCallbackRegistered) {
          this.$_unregisterSelectSegmentsCallback();
        }
        this.$_registerSelectSegmentsCallback(referFrom, registerSelectSegmentsCallbackButtonElement);
      }
    },

    $_openFilterMenu() {
      this.$refs.noteEditorFilterMenu.enable();
    },

    $_unregisterSelectSegmentsCallback() {
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement !== undefined) {
        this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.remove('attached-reference');
        this.$data.$_registerSelectSegmentsCallbackButtonElement = undefined;
      }
      this.unregisterSelectSegmentsCallback();
    },

    $_registerSelectSegmentsCallback(referFrom, registerSelectSegmentsCallbackButtonElement) {
      this.$data.$_registerSelectSegmentsCallbackButtonElement = registerSelectSegmentsCallbackButtonElement;
      this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.add('attached-reference');
      this.registerSelectSegmentsCallback((selectedTimelineSegmentIds) => {
        for (let selectedTimelineSegmentId of selectedTimelineSegmentIds) {
          let foundReferenceId = undefined;
          for (let [ referenceId, reference ] of Object.entries(referencePool.getAllReferences())) {
            if (reference.referTo.isSameAs(selectedTimelineSegmentId) && (reference.refClass === 'Note')) {
              foundReferenceId = referenceId;
              break;
            }
          }
          if (foundReferenceId) {
            this.unregisterReferences(foundReferenceId);
          } else {
            let reference = new Reference('Note', referFrom, selectedTimelineSegmentId);
            this.registerReferences(reference);
          }
        }
      });
    },

    $_applyFilter(filter) {
      this.$data.$_filter = filter;
    },

    $_refreshTimelineSegmentIdsRefferingTo() {
      let timelineSegmentIdsReferringTo = {};
      for (let [ referenceId, reference ] of Object.entries(referencePool.getAllReferences())) {
        if (reference.refClass === 'Note') {
          let noteTimelineSegmentMergedId = reference.referFrom.mergedId;
          if (!Object.keys(timelineSegmentIdsReferringTo).includes(noteTimelineSegmentMergedId)) {
            timelineSegmentIdsReferringTo[noteTimelineSegmentMergedId] = {};
          }
          timelineSegmentIdsReferringTo[noteTimelineSegmentMergedId][referenceId] = reference.referTo;
        }
      }
      this.$data.$_timelineSegmentIdsReferringTo = timelineSegmentIdsReferringTo;
    },
  },
};
</script>
